import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Collapse,
  Link,
} from '@mui/material';
import UploadIcon from '@mui/icons-material/Upload';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

interface ApplovinUploadDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (templateCreativeSetId: string, campaignId: string) => void;
  adCount: number;
  isUploading?: boolean;
}

const ApplovinUploadDialog: React.FC<ApplovinUploadDialogProps> = ({
  open,
  onClose,
  onConfirm,
  adCount,
  isUploading = false,
}) => {
  const [templateCreativeSetId, setTemplateCreativeSetId] = useState('');
  const [campaignId, setCampaignId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    if (open) {
      setTemplateCreativeSetId('');
      setCampaignId('');
      setError(null);
    }
  }, [open]);

  const handleSubmit = () => {
    setError(null);

    const trimmedTemplate = templateCreativeSetId.trim();
    const trimmedCampaign = campaignId.trim();

    if (!trimmedTemplate) {
      setError('Creative Set ID is required');
      return;
    }
    if (!/^\d+$/.test(trimmedTemplate)) {
      setError('Creative Set ID must be numeric. Copy the creativeSetId from the URL in the AppLovin dashboard.');
      return;
    }
    if (!trimmedCampaign) {
      setError('Campaign ID is required');
      return;
    }
    if (!/^\d+$/.test(trimmedCampaign)) {
      setError('Campaign ID must be numeric. Copy the campaignId from the URL in the AppLovin dashboard.');
      return;
    }

    onConfirm(trimmedTemplate, trimmedCampaign);
  };

  return (
    <Dialog
      open={open}
      onClose={isUploading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <UploadIcon sx={{ color: '#00B460' }} />
        Upload to AppLovin
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
          {adCount === 1
            ? 'Select which creative set to clone and which campaign to attach the new creative to.'
            : `Upload ${adCount} ads to AppLovin. Select which creative set to clone and which campaign to attach the new creatives to.`}
        </Typography>

        <Alert
          severity="info"
          icon={<InfoOutlinedIcon fontSize="small" />}
          sx={{ mb: 2, cursor: 'pointer' }}
          onClick={() => setShowHelp(!showHelp)}
        >
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Where do I find these? {showHelp ? '▴' : '▾'}
          </Typography>
          <Collapse in={showHelp}>
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                Open the{' '}
                <Link href="https://ads.applovin.com" target="_blank" rel="noopener">
                  AppLovin dashboard
                </Link>
                {' '}and copy the numeric ID from the URL.
              </Typography>

              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Campaign ID
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Open a campaign and find <code>campaignId</code> in the URL:
              </Typography>
              <Box sx={{ px: 1.5, py: 1, bgcolor: 'grey.100', borderRadius: 1, mb: 1.5, overflowX: 'auto' }}>
                <Typography variant="caption" component="pre" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all', m: 0 }}>
                  ads.applovin.com/campaigns?...&campaignId=<strong>1700738</strong>&...
                </Typography>
              </Box>

              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Creative Set ID
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Open a creative set and find <code>creativeSetId</code> in the URL:
              </Typography>
              <Box sx={{ px: 1.5, py: 1, bgcolor: 'grey.100', borderRadius: 1, mb: 1, overflowX: 'auto' }}>
                <Typography variant="caption" component="pre" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all', m: 0 }}>
                  ads.applovin.com/creative-set/edit?...&creativeSetId=<strong>1000467527</strong>&...
                </Typography>
              </Box>
            </Box>
          </Collapse>
        </Alert>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <TextField
            label="Creative Set ID"
            value={templateCreativeSetId}
            onChange={(e) => setTemplateCreativeSetId(e.target.value)}
            fullWidth
            placeholder="e.g. 1000467527"
            helperText="The numeric creativeSetId from the AppLovin dashboard URL"
            disabled={isUploading}
            autoFocus
          />

          <TextField
            label="Campaign ID"
            value={campaignId}
            onChange={(e) => setCampaignId(e.target.value)}
            fullWidth
            placeholder="e.g. 1700738"
            helperText="The numeric campaignId from the AppLovin dashboard URL"
            disabled={isUploading}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={isUploading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isUploading}
          startIcon={isUploading ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
          sx={{
            backgroundColor: '#00B460',
            '&:hover': { backgroundColor: '#009950' },
          }}
        >
          {isUploading ? 'Uploading...' : 'Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ApplovinUploadDialog;
